import { WorkCalendar } from "@/engine/calendar";
import { remainingInfo } from "@/engine/remaining";
import type { Project, Schedule } from "@/engine/types";
import { countLinkedExecution } from "@/features/tasks/task-query";
import { EXCEL_DATE_FORMAT, formatDate } from "@/lib/date-format";
import { formatPredecessors } from "@/store/tree-ops";
import { saveBytes } from "@/desktop/file-io";

const XLSX_MIME = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const HEADERS = ["행", "WBS", "작업", "유형", "담당", "M/M", "기간", "시작", "종료", "여유", "진척", "선행", "태스크", "남은 기간", "제약", "마감", "산출물", "인수 기준", "메모"] as const;

function excelDate(value: string): Date {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year!, month! - 1, day!);
}

const DATE_COLUMNS = [8, 9, 16] as const;

function safeFilename(value: string): string {
  const cleaned = value.replace(/[\\/:*?"<>|]/g, "_").trim();
  return cleaned || "플랜";
}

export async function exportScheduleExcel(project: Project, schedule: Schedule): Promise<void> {
  const ExcelJS = await import("exceljs");
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Super Gantt";
  workbook.created = new Date();

  const sheet = workbook.addWorksheet("일정", {
    views: [{ state: "frozen", xSplit: 3, ySplit: 1 }],
    properties: { outlineProperties: { summaryBelow: false, summaryRight: false } },
  });

  sheet.addRow(HEADERS);
  sheet.columns = [
    { key: "row", width: 6 },
    { key: "wbs", width: 10 },
    { key: "name", width: 36 },
    { key: "type", width: 10 },
    { key: "assignee", width: 14 },
    { key: "mm", width: 10 },
    { key: "duration", width: 10 },
    { key: "start", width: 13 },
    { key: "finish", width: 13 },
    { key: "float", width: 10 },
    { key: "progress", width: 10 },
    { key: "predecessors", width: 14 },
    { key: "tasks", width: 10 },
    { key: "remaining", width: 10 },
    { key: "constraint", width: 15 },
    { key: "deadline", width: 13 },
    { key: "deliverable", width: 24 },
    { key: "acceptance", width: 24 },
    { key: "note", width: 32 },
  ];

  const taskById = new Map(project.tasks.map((task) => [task.id, task]));
  const calendar = new WorkCalendar(project.calendar);
  schedule.order.forEach((id, index) => {
    const task = taskById.get(id);
    const scheduled = schedule.tasks.get(id);
    if (!task || !scheduled) return;

    const row = sheet.addRow([
      index + 1,
      scheduled.wbs,
      task.name,
      scheduled.isSummary ? "단계" : scheduled.isMilestone ? "마일스톤" : "작업",
      task.assignee ?? "",
      scheduled.isSummary ? null : task.plannedMM ?? Math.round((scheduled.duration / 21) * 10) / 10,
      scheduled.duration,
      excelDate(scheduled.start),
      excelDate(scheduled.finish),
      scheduled.isSummary ? null : scheduled.totalFloat,
      scheduled.percentComplete / 100,
      formatPredecessors(project, id),
      (() => {
        const { done, total } = countLinkedExecution(id, project.executionTasks, schedule);
        return total === 0 ? "" : `${done}/${total}`;
      })(),
      (() => {
        const r = remainingInfo(scheduled, project.statusDate, calendar);
        return r.kind === "done" ? null : r.days;
      })(),
      task.constraint ? `${task.constraint.type} ${formatDate(task.constraint.date, "numeric")}${task.constraint.auto ? " (자동)" : ""}` : "",
      task.deadline ? excelDate(task.deadline) : null,
      task.deliverable ?? "",
      task.acceptance ?? "",
      task.note ?? "",
    ]);

    row.outlineLevel = Math.min(scheduled.depth, 7);
    row.getCell(3).alignment = { vertical: "middle", indent: Math.min(scheduled.depth, 15) };
    if (scheduled.isSummary) {
      row.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF3F4F6" } };
    }
    if (scheduled.critical) {
      row.getCell(2).font = { color: { argb: "FFB42318" } };
    }
  });

  const header = sheet.getRow(1);
  header.height = 22;
  header.alignment = { vertical: "middle" };
  header.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE8EAED" } };
  header.font = { color: { argb: "FF424242" } };

  sheet.autoFilter = { from: "A1", to: `S${Math.max(1, sheet.rowCount)}` };
  sheet.getColumn(1).numFmt = "0";
  sheet.getColumn(6).numFmt = "0.0";
  sheet.getColumn(7).numFmt = '0"d"';
  for (const col of DATE_COLUMNS) sheet.getColumn(col).numFmt = EXCEL_DATE_FORMAT;
  sheet.getColumn(10).numFmt = '0"d"';
  sheet.getColumn(11).numFmt = "0%";
  sheet.getColumn(14).numFmt = '0"d"';

  for (const row of sheet.getRows(1, sheet.rowCount) ?? []) {
    row.eachCell({ includeEmpty: true }, (cell) => {
      cell.border = {
        bottom: { style: "hair", color: { argb: "FFD9D9D9" } },
        right: { style: "hair", color: { argb: "FFE5E5E5" } },
      };
      if (typeof cell.col === "number" && [1, 6, 7, 8, 9, 10, 11, 13, 14, 16].includes(cell.col)) {
        cell.alignment = { ...cell.alignment, horizontal: "right", vertical: "middle" };
      }
    });
  }

  const buffer = await workbook.xlsx.writeBuffer();
  await saveBytes(`${safeFilename(project.name)}_일정.xlsx`, buffer as ArrayBuffer, XLSX_MIME);
}
